import useAuthTransition from '@aurora/shared-client/components/authentication/useAuthTransition';
import Panel, { PanelBody, PanelHeader } from '@aurora/shared-client/components/common/Panel/Panel';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import clientAuthenticationHelper from '@aurora/shared-client/helpers/authentication/ClientAuthenticationHelper';
import type {
  PageAndEmptyParams,
  UserSettingsPageAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import {
  EndUserComponent,
  EndUserPages,
  EndUserPathParams
} from '@aurora/shared-types/pages/enums';
import { AuthTransitionType } from '@aurora/shared-utils/types/enums';
import React, { useId } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { UserSettingsTab } from '../../../types/enums';
import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import useTranslation from '../../useTranslation';
import type { RegistrationFormData } from '@aurora/shared-client/components/authentication/RegistrationForm/RegistrationForm';
import RegistrationForm from '@aurora/shared-client/components/authentication/RegistrationForm/RegistrationForm';
import useEndUserNextPage from '@aurora/shared-client/routes/useEndUserNextPage';

interface Props extends WidgetProps {
  /**
   * Whether ReCaptcha should be used.
   */
  useReCaptcha?: boolean;
}

/**
 * Registration Widget Component used for Registration page.
 *
 * @author Stephen McLaughry, Willi Hyde
 */
const RegistrationWidget: WidgetFC<Props> = ({ useReCaptcha }) => {
  const cx = useClassNameMapper();
  const { Link } = useEndUserRoutes();
  const { nextRoute } = useEndUserNextPage();
  const handleAuthTransition = useAuthTransition(nextRoute);
  const {
    formatMessage,
    FormattedMessage,
    loading: textLoading
  } = useTranslation(EndUserComponent.REGISTRATION_WIDGET);
  const uid = useId();
  const { addToast } = useToasts();
  const toastProps: ToastProps = {
    toastVariant: ToastVariant.FLYOUT,
    alertVariant: ToastAlertVariant.SUCCESS,
    title: formatMessage('welcome.invited.user.title'),
    message: () => {
      return (
        <FormattedMessage
          id="welcome.invited.user.body"
          values={{
            link: function renderChunks(chunks): React.ReactNode {
              return (
                <Link<UserSettingsPageAndParams>
                  legacyBehavior={true}
                  route={EndUserPages.UserSettingsPage}
                  params={{ [EndUserPathParams.USER_SETTINGS_TAB]: UserSettingsTab.PERSONAL }}
                  passHref
                >
                  <a>{chunks}</a>
                </Link>
              );
            }
          }}
        />
      );
    },
    autohide: true,
    delay: 4000,
    id: `welcome-to-community-${uid}`
  };

  if (textLoading) {
    return null;
  }

  return (
    <main className={cx('lia-g-auth-wrapper')}>
      <Panel className={cx('lia-g-auth-panel')} data-testid={uid}>
        <PanelHeader className={cx('lia-g-auth-panel-header')}>
          <h1 className={cx('lia-g-auth-panel-h1')}>{formatMessage('title')}</h1>
        </PanelHeader>
        <PanelBody className={cx('lia-g-auth-panel-body')}>
          <RegistrationForm
            onRegistration={async (formData: RegistrationFormData, inviteToken): Promise<void> => {
              const loginResult = await clientAuthenticationHelper.loginFromRegistrationForm(
                formData
              );
              if (loginResult?.success) {
                const { login, rememberPassword, custom } = formData;
                await handleAuthTransition(AuthTransitionType.REGISTRATION, {
                  sso: false,
                  data: {
                    login,
                    rememberPassword,
                    custom
                  }
                });
                if (inviteToken) {
                  addToast(toastProps);
                }
              }
            }}
            useReCaptcha={useReCaptcha}
          >
            <div className={cx('lia-g-auth-nav-section')}>
              <FormattedMessage
                id="nav.text.login"
                values={{
                  link: function renderChunks(chunks): React.ReactNode {
                    return (
                      <Link<PageAndEmptyParams>
                        route={EndUserPages.LoginPage}
                        legacyBehavior={true}
                      >
                        <a>{chunks}</a>
                      </Link>
                    );
                  }
                }}
              />
            </div>
          </RegistrationForm>
        </PanelBody>
      </Panel>
    </main>
  );
};

export default RegistrationWidget;
